import {
  Content,
  DropdownMenuContentProps as RadixContentProps,
  DropdownMenuSubContentProps as RadixSubContentProps,
  SubContent,
} from '@radix-ui/react-dropdown-menu';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';

export interface MenuContentProps extends RadixContentProps {
  children: React.ReactNode;
}

interface MenuSubContentProps extends RadixSubContentProps {
  children: React.ReactNode;
}

const useStyles = makeStyles()(theme => ({
  content: {
    backgroundColor: palette.gray_900,
    padding: theme.spacing(0.5),
    boxShadow: theme.shadows[8],
    borderRadius: theme.spacing(0.5),
    zIndex: theme.zIndex.tooltip,
    '& div[role="menuitem"]': {
      color: theme.palette.common.white,
      borderRadius: theme.spacing(0.5),
      // If is a sub trigger w menu, adjust space for chevron icon
      '&[aria-haspopup="menu"]': {
        '& > *': {
          padding: theme.spacing(1, 0, 1, 1),
        },
      },
      '& > *': {
        minWidth: theme.spacing(16),
        padding: theme.spacing(1),
      },
      '&:not([data-disabled=""])': {
        backgroundColor: palette.gray_900,
        outline: 'none',
        '&[data-highlighted]': {
          backgroundColor: palette.gray_700,
          cursor: 'pointer',
          '&:not(:hover)': {
            '&:not([data-state="open"])': {
              outline: `2px solid ${palette.mint_200}`,
              outlineOffset: -1,
            },
          },
        },
      },
      '&[data-disabled=""]': {
        color: palette.gray_400,
      },
    },
  },
}));

export const MenuSubContent = React.forwardRef<
  HTMLDivElement,
  MenuSubContentProps
>((props, forwardRef) => {
  const { children, className, ...rest } = props;
  const { classes, cx } = useStyles();
  return (
    <SubContent
      ref={forwardRef}
      className={cx(classes.content, className)}
      {...rest}
    >
      {children}
    </SubContent>
  );
});

const MenuContent = React.forwardRef<HTMLDivElement, MenuContentProps>(
  (props, forwardRef) => {
    const { children, className, ...rest } = props;
    const { classes, cx } = useStyles();

    return (
      <Content
        ref={forwardRef}
        className={cx(className, classes.content)}
        {...rest}
      >
        {children}
      </Content>
    );
  }
);

export default MenuContent;
